import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { getMcqDisciplineCoverage, DIFFICULTY_LABELS, DIFFICULTY_BADGE_BG, DIFFICULTY_BADGE_TEXT } from '../../lib/mcq';

const disciplineLabels = { medical: 'Medical', pharmacy: 'Pharmacy', nursing: 'Nursing' };
const disciplineBadgeBg = { medical: 'danger', pharmacy: 'info', nursing: 'success' };

const renderBadges = (exercise, disciplineMode, coverage, showTierBadge) => (
  <>
    {exercise.category
      ? <Badge bg="info"><i className="bi bi-tag-fill me-1" />{exercise.category}</Badge>
      : null}
    {exercise.difficulty
      ? <Badge bg={DIFFICULTY_BADGE_BG[exercise.difficulty]} text={DIFFICULTY_BADGE_TEXT[exercise.difficulty]}>
          {DIFFICULTY_LABELS[exercise.difficulty]}
        </Badge>
      : null}
    {exercise.payload?.mode === 'challenge'
      ? <Badge bg="primary"><i className="bi bi-controller me-1" />Challenge</Badge>
      : null}
    {showTierBadge && (exercise.freeTierEligible
      ? <Badge bg="success">Free</Badge>
      : <Badge bg="warning" text="dark"><i className="bi bi-lock-fill me-1" />Paid only</Badge>)}
    {disciplineMode && coverage.length === 0 ? <Badge bg="light" text="dark">All student groups</Badge> : null}
    {disciplineMode && coverage.map((c) => <Badge key={c} bg={disciplineBadgeBg[c]}>{disciplineLabels[c]}</Badge>)}
  </>
);

const StudentMcqCatalog = ({ exercises = [], onStart, onBack, initialExerciseId, onInitialExerciseConsumed, tenant }) => {
  const showTierBadge = tenant?.tenantType === 'central_students';
  const sorted = useMemo(() => [...exercises].sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''))), [exercises]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!initialExerciseId) return;
    const match = exercises.find((e) => e.id === initialExerciseId);
    if (match) setSelected(match);
    if (typeof onInitialExerciseConsumed === 'function') onInitialExerciseConsumed();
  }, [initialExerciseId, exercises, onInitialExerciseConsumed]);

  const closeDetail = () => setSelected(null);
  const startSelected = () => {
    if (selected && onStart) onStart(selected);
    setSelected(null);
  };

  const selectedDisciplineMode = Boolean(selected?.payload?.disciplineMode);
  const selectedCoverage = selectedDisciplineMode ? getMcqDisciplineCoverage(selected?.payload) : [];
  const selectedTemplateCount = (selected?.payload?.templates || []).length;
  const selectedPerSession = Math.min(selectedTemplateCount, selected?.payload?.settings?.questionCount || selectedTemplateCount);

  return (
    <div className="student-page">
      <Container className="mt-4 student-page__content">
        <div className="student-dashboard-header shadow-sm rounded mb-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h2 className="mb-2">Question bank</h2>
              <p className="student-dashboard-header__copy mb-0">Recall practice sets. Tap a set to see what it covers, then start it. Each run draws a random subset.</p>
              {onBack ? (
                <Button type="button" variant="outline-primary" className="btn-sm mt-2" onClick={onBack}>
                  <i className="bi bi-arrow-left" aria-hidden="true" />{' '}
                  Back
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {sorted.length === 0 ? (
          <Alert variant="info">No question sets available yet. Check back soon.</Alert>
        ) : (
          <Row className="g-3">
            {sorted.map((exercise) => {
              const disciplineMode = Boolean(exercise.payload?.disciplineMode);
              const coverage = disciplineMode ? getMcqDisciplineCoverage(exercise.payload) : [];
              const templateCount = (exercise.payload?.templates || []).length;
              const perSession = Math.min(templateCount, exercise.payload?.settings?.questionCount || templateCount);
              return (
                <Col md={6} lg={4} key={exercise.id}>
                  <Card
                    as="button"
                    type="button"
                    className="h-100 container-shadow student-exercise-card text-start"
                    onClick={() => setSelected(exercise)}
                  >
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {renderBadges(exercise, disciplineMode, coverage, showTierBadge)}
                      </div>
                      <h5 className="mb-2">{exercise.title}</h5>
                      <div className="small text-muted mt-auto d-flex justify-content-between align-items-center gap-2">
                        <span>{perSession} questions per session</span>
                        {exercise.personalBest ? (
                          <span className="student-exercise-card__best"><i className="bi bi-trophy-fill" /> {exercise.personalBest.score}%</span>
                        ) : null}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>

      <Modal show={Boolean(selected)} onHide={closeDetail} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selected?.title || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-1 mb-3">
            {selected ? renderBadges(selected, selectedDisciplineMode, selectedCoverage, showTierBadge) : null}
          </div>
          <p className="mb-3">{selected?.summary || 'No description set.'}</p>
          <div className="small text-muted">{selectedPerSession} questions per session, drawn from {selectedTemplateCount} in the pool.</div>
          {selected?.personalBest ? (
            <div className="small text-muted mt-2">
              <i className="bi bi-trophy-fill text-warning me-1" />
              Personal best: <strong>{selected.personalBest.score}%</strong>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={closeDetail}>Close</Button>
          <Button type="button" variant="primary" onClick={startSelected}>Start set</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentMcqCatalog;
